// @ts-nocheck
import type { AxiosResponse } from 'axios';
import type { Action } from 'redux-actions';
import { call, fork, put, select, takeLatest } from 'redux-saga/effects';

import * as CompaniesSelector from 'app/selectors/companiesSelector.ts';
import type { CompanyDto, CreateCompanyCommand, DeleteCompanyCommand } from '@/api-gen/api.schemas';
import { 
  companiesFilterCompanies,
  companiesAddCompany,
  companiesEditCompany,
  companiesDeleteCompany
} from '@/api-gen/api';
import { CompanyManagementActions } from 'app/actions/companyManagment/companyManagementActions.ts';

export function* getCompanies() {
  yield put(CompanyManagementActions.setCompaniesAreLoading(true));
  try {
    const response = yield call(companiesFilterCompanies);
    const data = response?.data ?? response;
    yield put(CompanyManagementActions.setCompanies(data));
  } finally {
    yield put(CompanyManagementActions.setCompaniesAreLoading(false));
  }
}

function* addCompany(action: Action<CreateCompanyCommand>) {
  yield put(CompanyManagementActions.setCompaniesAreLoading(true));
  try {
    const response = yield call(companiesAddCompany, action.payload);
    const created = response?.data ?? response;

    const currentCompanies: CompanyDto[] = yield select(CompaniesSelector.companiesSelector);
    yield put(
      CompanyManagementActions.setCompanies([
        ...currentCompanies,
        {
          id: created.id,
          name: created.name
        }
      ])
    );
  } finally {
    yield put(CompanyManagementActions.setCompaniesAreLoading(false));
  }
}

function* editCompany(action: Action<CompanyDto>) {
  yield put(CompanyManagementActions.setCompaniesAreLoading(true));
  try {
    const response = yield call(companiesEditCompany, action.payload);
    const updated = response?.data ?? response;

    const currentCompanies: CompanyDto[] = yield select(CompaniesSelector.companiesSelector);
    const updatedCompanies = currentCompanies.map((c) =>
      c.id === updated.id ? { ...c, name: updated.name } : c
    );

    yield put(CompanyManagementActions.setCompanies(updatedCompanies));
  } finally {
    yield put(CompanyManagementActions.setCompaniesAreLoading(false));
  }
}

function* deleteCompany(action: Action<CompanyManagementActions.CompanyDeletionActionType>) {
  yield put(CompanyManagementActions.setCompaniesAreLoading(true));
  try {
    const deleteCommand: DeleteCompanyCommand = {
      id: action.payload.id,
      onCompanyDeleteAction: action.payload.deletionAction,
      destinationCompanyId: action.payload.moveToCompanyId
    };
    yield call(companiesDeleteCompany, deleteCommand);

    const currentCompanies: CompanyDto[] = yield select(CompaniesSelector.companiesSelector);
    yield put(CompanyManagementActions.setCompanies(currentCompanies.filter((c) => c.id !== action.payload.id)));
  } finally {
    yield put(CompanyManagementActions.setCompaniesAreLoading(false));
  }
}

function* watchGetCompanies() {
  yield takeLatest(CompanyManagementActions.Type.GET_COMPANIES, getCompanies);
}

function* watchAddCompany() {
  yield takeLatest(CompanyManagementActions.Type.ADD_COMPANY, addCompany);
}

function* watchEditCompany() {
  yield takeLatest(CompanyManagementActions.Type.EDIT_COMPANY, editCompany);
}

function* watchDeleteCompany() {
  yield takeLatest(CompanyManagementActions.Type.DELETE_COMPANY, deleteCompany);
}

export function* watchCompanyManagement(): Generator {
  yield fork(watchGetCompanies);
  yield fork(watchAddCompany);
  yield fork(watchEditCompany);
  yield fork(watchDeleteCompany);
}
